import { useMemo } from 'react'
import { useWatch, type Control } from 'react-hook-form'
import { FormValues, JsonFormPropertyV2, RequireWhenRule } from './types'
import { evaluateRule } from './utils'

type ConditionalRuleKey = 'readOnlyWhen' | 'visibleWhen'

function collectDependencyFieldNames(fieldsWithRule: JsonFormPropertyV2[], ruleKey: ConditionalRuleKey): string[] {
  const names = new Set<string>()
  for (const field of fieldsWithRule) {
    const rule = field[ruleKey] as RequireWhenRule | undefined
    if (!rule) continue
    if (rule.anyOf) Object.keys(rule.anyOf).forEach((key) => names.add(key))
    if (rule.allOf) Object.keys(rule.allOf).forEach((key) => names.add(key))
  }
  return Array.from(names)
}

/**
 * Watches form values and evaluates conditional rules (`readOnlyWhen`, `visibleWhen`, etc.)
 * across all fields. Returns a Set of field IDs whose rule currently matches.
 */
export function useConditionalRuleFields(
  properties: JsonFormPropertyV2[],
  ruleKey: ConditionalRuleKey,
  control: Control<FormValues>
): Set<string> {
  const fieldsWithRule = useMemo(
    () => properties.filter((property) => property[ruleKey]),
    [properties, ruleKey]
  )

  const depFieldNames = useMemo(
    () => collectDependencyFieldNames(fieldsWithRule, ruleKey),
    [fieldsWithRule, ruleKey]
  )

  const watchedValues = useWatch({ control, name: depFieldNames as never[] })

  return useMemo(() => {
    if (fieldsWithRule.length === 0) return new Set<string>()

    const data: Record<string, unknown> = {}
    depFieldNames.forEach((name, index) => {
      data[name] = (watchedValues as unknown[])[index]
    })

    const matchedFieldIds = new Set<string>()
    for (const field of fieldsWithRule) {
      const rule = field[ruleKey] as RequireWhenRule
      if (evaluateRule(data, rule)) {
        matchedFieldIds.add(field.id)
      }
    }
    return matchedFieldIds
  }, [fieldsWithRule, depFieldNames, watchedValues, ruleKey])
}
